/**
 * AI Settings Page
 *
 * Configuration page for AI models and preferences
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/services/api';
import ResearchSearchSettingsCard from '@/components/ai/ResearchSearchSettingsCard';
// import { useLanguage } from '../../state/language/LanguageProvider';

interface AIModelConfiguration {
  id: string;
  modelName: string;
  modelVersion: string;
  displayName: string;
  description: string;
  isDefault: boolean;
  isActive: boolean;
  configuration: {
    endpoint?: string;
    apiKey?: string;
    api_key?: string;
    model?: string;
    ollamaModel?: string;
  };
  usageLimit?: number;
  costPerRequest?: number;
  costPerToken?: number;
}

type AISettingsScope = 'personal' | 'tenant';

const AISettingsPage: React.FC<{ scope?: AISettingsScope }> = ({ scope = 'personal' }) => {
  // const { t } = useLanguage();
  const [configurations, setConfigurations] = useState<AIModelConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiSettings, setAiSettings] = useState({
    maxTokensPerConversation: 8000,
    summarizationStrategy: 'intelligent',
    veryOldSummaryType: 'very_brief',
    oldSummaryType: 'brief',
    recentSummaryType: 'very_brief',
    modelSelectionStrategy: 'cost_optimized',
    conversationTimeoutMinutes: 60,
    maxConversationLength: 100
  });
  const [saving, setSaving] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configuringModel, setConfiguringModel] = useState<AIModelConfiguration | null>(null);
  const [configForm, setConfigForm] = useState({
    apiKey: '',
    endpoint: '',
    currentModel: ''
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    modelName: '',
    displayName: '',
    description: '',
    endpoint: '',
    model: '',
    apiKey: ''
  });

  useEffect(() => {
    loadConfigurations();
    loadSavedSettings();
  }, [scope]);

  const loadSavedSettings = async () => {
    try {
      const response = await api.get('ai-settings');
      if (response.data) setAiSettings(response.data);
    } catch (err) {
      console.error('Error loading saved settings:', err);
    }
  };

  const loadConfigurations = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('ai/config/models', { params: { scope } });
      setConfigurations(response.data ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefault = async (configId: string) => {
    try {
      await api.put('ai/config/models', { defaultModel: configId, scope });
      await loadConfigurations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const testModelAvailability = async (config: any) => {
    if (config.modelName === 'ollama') {
        const response = await api.get('ai-settings/ollama/status');
        const status = response.data;
        if (!status?.available) {
          throw new Error('Ollama service is not running');
        }
        return true;
      } else {
        // Any other LLM provider: require API key to be set (not placeholder)
        const key = config.configuration?.api_key ?? config.configuration?.apiKey;
        if (!key || typeof key !== 'string' || key.length < 10 || /^[\w-]+\.\.\.$/.test(key)) {
          throw new Error('API key is not configured for this provider');
        }
        return true;
      }
  };

  const handleToggleActive = async (configId: string) => {
    const config = configurations.find(c => c.id === configId);
    if (!config) return;
    const newActiveState = !config.isActive;
    if (newActiveState) {
      try {
        await testModelAvailability(config);
      } catch (error) {
        alert(`Cannot activate model: ${error instanceof Error ? error.message : String(error)}. Please configure the model first.`);
        return;
      }
    }
    try {
      setSaving(true);
      setError(null);
      await api.put('ai/config/models', { id: configId, isActive: newActiveState });
      await loadConfigurations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAISettings = async () => {
    try {
      setSaving(true);
      
            
      // Filter out fields that shouldn't be sent to backend
      const settingsToSend = {
        maxTokensPerConversation: aiSettings.maxTokensPerConversation,
        summarizationStrategy: aiSettings.summarizationStrategy,
        veryOldSummaryType: aiSettings.veryOldSummaryType,
        oldSummaryType: aiSettings.oldSummaryType,
        recentSummaryType: aiSettings.recentSummaryType,
        modelSelectionStrategy: aiSettings.modelSelectionStrategy,
        conversationTimeoutMinutes: aiSettings.conversationTimeoutMinutes,
        maxConversationLength: aiSettings.maxConversationLength
      };
      
      console.log('Settings being sent:', JSON.stringify(settingsToSend, null, 2));
      
      await api.post('ai-settings', settingsToSend);
      alert('AI settings saved successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };


  const handleConfigureModel = (config: AIModelConfiguration) => {
    setConfiguringModel(config);
    const cfg = config.configuration as Record<string, unknown>;
    const apiKey = (cfg['api_key'] ?? cfg['apiKey'] ?? '') as string;
    const model = (cfg['model'] ?? cfg['ollamaModel'] ?? '') as string;
    setConfigForm({
      apiKey: apiKey || '',
      endpoint: config.configuration.endpoint || '',
      currentModel: model
    });
    setShowConfigModal(true);
  };

  const handleCloseConfigModal = () => {
    setShowConfigModal(false);
    setConfiguringModel(null);
    setConfigForm({
      apiKey: '',
      endpoint: '',
      currentModel: ''
    });
  };

  const handleOpenAddModal = () => {
    setAddForm({
      modelName: '',
      displayName: '',
      description: '',
      endpoint: '',
      model: '',
      apiKey: ''
    });
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleAddModel = async () => {
    const modelName = addForm.modelName.trim();
    const displayName = addForm.displayName.trim();
    if (!modelName || !displayName) {
      alert('Provider name and display name are required.');
      return;
    }
    try {
      setSaving(true);
      const configuration: Record<string, string> = {};
      if (addForm.endpoint.trim()) configuration.endpoint = addForm.endpoint.trim();
      if (addForm.model.trim()) configuration.model = addForm.model.trim();
      if (addForm.apiKey.trim()) configuration.api_key = addForm.apiKey;
      await api.post('ai/config/models', {
        modelName,
        displayName,
        description: addForm.description.trim() || undefined,
        configuration,
        setAsDefault: false,
        ...(scope === 'tenant' ? { scope: 'tenant' } : {}),
      });
      await loadConfigurations();
      setShowAddModal(false);
      alert('Model added. You can configure it further or set it as default.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add model');
      alert(err instanceof Error ? err.message : 'Failed to add model');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);

      // Update the model configuration (same path for all providers)
      if (configuringModel) {
        const configuration: Record<string, string> = {
          endpoint: configForm.endpoint?.trim() || '',
          model: configForm.currentModel?.trim() || ''
        };
        if (configForm.apiKey && configForm.apiKey.length >= 10 && !/^[\w-]+\.\.\.$/.test(configForm.apiKey)) {
          configuration.api_key = configForm.apiKey;
        }
        await api.put('ai/config/models', {
          id: configuringModel.id,
          configuration
        });

        if (configuringModel.modelName === 'ollama') {
          await api.put('ai/ollama/model', { modelName: configForm.currentModel });
        }
        if (configForm.endpoint) {
          try {
            await api.post('ai-settings', { aiEndpoint: configForm.endpoint });
          } catch {
            console.warn('Failed to update AI endpoint in settings');
          }
        }
      }

      // Reload configurations to get updated data
      await loadConfigurations();

      alert('Model configuration updated successfully!');
      setShowConfigModal(false);
      setConfiguringModel(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update model configuration');
    } finally {
      setSaving(false);
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004C94]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-[#004D99]">
                  {scope === 'tenant' ? 'Tenant AI models' : 'AI Settings'}
                </h1>
                <p className="mt-2 text-[#95C4FF]">
                  {scope === 'tenant'
                    ? 'Configure AI models for all users in your tenant. These appear in the model list for planning and other AI features.'
                    : 'Configure AI models and preferences for your organization'}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {scope === 'tenant' ? (
                    <>Users can add personal models in <Link to="/ai/settings" className="text-[#004C94] hover:underline">AI Settings</Link>. Platform: <Link to="/admin/ai/settings" className="text-[#004C94] hover:underline">Admin AI Settings</Link></>
                  ) : (
                    <>Admins: free-usage limits and default LLM for planning →{' '}
                      <Link to="/admin/ai/settings" className="text-[#004C94] hover:underline">Admin AI Settings</Link></>
                  )}
                </p>
              </div>
              <button
                onClick={handleOpenAddModal}
                className="bg-[#004C94] text-white px-4 py-2 rounded-md hover:bg-[#004C94]/80 transition-colors"
              >
                Add Model Configuration
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        {scope === 'tenant' ? <ResearchSearchSettingsCard mode="tenant" /> : null}

        {/* AI Settings Section */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              AI Settings
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Tokens per Conversation
                </label>
                <input
                  type="number"
                  value={aiSettings.maxTokensPerConversation}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                                        setAiSettings({...aiSettings, maxTokensPerConversation: newValue});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1000"
                  max="128000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Summarization Strategy
                </label>
                <select 
                  value={aiSettings.summarizationStrategy}
                  onChange={(e) => setAiSettings({...aiSettings, summarizationStrategy: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="intelligent">Intelligent</option>
                  <option value="simple">Simple</option>
                  <option value="none">None</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model Selection Strategy
                </label>
                <select 
                  value={aiSettings.modelSelectionStrategy}
                  onChange={(e) => setAiSettings({...aiSettings, modelSelectionStrategy: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cost_optimized">Cost Optimized</option>
                  <option value="performance_optimized">Performance Optimized</option>
                  <option value="balanced">Balanced</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conversation Timeout (minutes)
                </label>
                <input
                  type="number"
                  value={aiSettings.conversationTimeoutMinutes}
                  onChange={(e) => setAiSettings({...aiSettings, conversationTimeoutMinutes: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="5"
                  max="120"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Very Old Summary Type
                </label>
                <select 
                  value={aiSettings.veryOldSummaryType}
                  onChange={(e) => setAiSettings({...aiSettings, veryOldSummaryType: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="very_brief">Very Brief</option>
                  <option value="brief">Brief</option>
                  <option value="detailed">Detailed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Old Summary Type
                </label>
                <select 
                  value={aiSettings.oldSummaryType}
                  onChange={(e) => setAiSettings({...aiSettings, oldSummaryType: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="very_brief">Very Brief</option>
                  <option value="brief">Brief</option>
                  <option value="detailed">Detailed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recent Summary Type
                </label>
                <select 
                  value={aiSettings.recentSummaryType}
                  onChange={(e) => setAiSettings({...aiSettings, recentSummaryType: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="very_brief">Very Brief</option>
                  <option value="brief">Brief</option>
                  <option value="detailed">Detailed</option>
                </select>
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Conversation Length
                </label>
                <input
                  type="number"
                  value={aiSettings.maxConversationLength}
                  onChange={(e) => setAiSettings({...aiSettings, maxConversationLength: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="10"
                  max="1000"
                />
              </div>
            </div>
            
            <div className="mt-6">
              <button 
                onClick={handleSaveAISettings}
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>


        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              AI Model Configurations
            </h3>
            
            {configurations.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  No AI model configurations found. Add your first model configuration to get started.
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {configurations.map((config) => (
                  <div
                    key={config.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="text-lg font-medium text-gray-900">
                            {config.displayName}
                          </h4>
                          {config.isDefault && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Default
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            config.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {config.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">
                          {config.description}
                        </p>
                        <div className="mt-2 text-sm text-gray-500">
                          <span className="font-medium">Model:</span> {config.modelName} v{config.modelVersion}
                        </div>
                        {config.configuration.model && (
                          <div className="mt-1 text-sm text-gray-500">
                            <span className="font-medium">Current Model:</span> {config.configuration.model}
                          </div>
                        )}
                        {config.configuration.endpoint && (
                          <div className="mt-1 text-sm text-gray-500">
                            <span className="font-medium">Endpoint:</span> {config.configuration.endpoint}
                          </div>
                        )}
                        {config.configuration.apiKey && (
                          <div className="mt-1 text-sm text-gray-500">
                            <span className="font-medium">API Key:</span> {config.configuration.apiKey}
                          </div>
                        )}
                        {config.costPerRequest && (
                          <div className="mt-1 text-sm text-gray-500">
                            <span className="font-medium">Cost:</span> ${config.costPerRequest} per request
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {!config.isDefault && config.isActive && (
                          <button
                            onClick={() => handleSetDefault(config.id)}
                            className="text-sm text-[#004C94] hover:text-[#004C94]/80 font-medium"
                          >
                            Set as Default
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleActive(config.id)}
                          className={`text-sm font-medium ${
                            config.isActive 
                              ? 'text-red-600 hover:text-red-500' 
                              : 'text-green-600 hover:text-green-500'
                          }`}
                        >
                          {config.isActive ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleConfigureModel(config)}
                          className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                        >
                          Configure
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Model Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add new LLM</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add any provider (e.g. OpenAI, Perplexity, Ollama, Claude). Use a short provider key as &quot;Provider name&quot;.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider name *</label>
                <input
                  type="text"
                  value={addForm.modelName}
                  onChange={(e) => setAddForm({ ...addForm, modelName: e.target.value })}
                  placeholder="e.g. openai, perplexity, ollama, anthropic"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display name *</label>
                <input
                  type="text"
                  value={addForm.displayName}
                  onChange={(e) => setAddForm({ ...addForm, displayName: e.target.value })}
                  placeholder="e.g. OpenAI GPT-4, Perplexity Sonar"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={addForm.description}
                  onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                  placeholder="Short description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint (optional)</label>
                <input
                  type="text"
                  value={addForm.endpoint}
                  onChange={(e) => setAddForm({ ...addForm, endpoint: e.target.value })}
                  placeholder="e.g. https://api.openai.com/v1 or http://localhost:11434"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model ID (optional)</label>
                <input
                  type="text"
                  value={addForm.model}
                  onChange={(e) => setAddForm({ ...addForm, model: e.target.value })}
                  placeholder="e.g. gpt-4, sonar, llama3.2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key (optional)</label>
                <input
                  type="password"
                  value={addForm.apiKey}
                  onChange={(e) => setAddForm({ ...addForm, apiKey: e.target.value })}
                  placeholder="For cloud APIs; leave blank for local (e.g. Ollama)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCloseAddModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddModel}
                disabled={saving}
                className="px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004C94]/90 disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add model'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && configuringModel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Configure {configuringModel.displayName}
            </h3>

            <div className="space-y-4">

              {/* Single form for all providers: endpoint, model, optional API key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Endpoint (base URL)
                </label>
                <input
                  type="text"
                  value={configForm.endpoint}
                  onChange={(e) => setConfigForm({...configForm, endpoint: e.target.value})}
                  placeholder="e.g. http://localhost:11434 or https://api.openai.com/v1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model ID
                </label>
                <input
                  type="text"
                  value={configForm.currentModel}
                  onChange={(e) => setConfigForm({...configForm, currentModel: e.target.value})}
                  placeholder="e.g. llama2, gpt-4, claude-3-sonnet, sonar"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key (optional)
                </label>
                <input
                  type="password"
                  value={configForm.apiKey}
                  onChange={(e) => setConfigForm({...configForm, apiKey: e.target.value})}
                  placeholder="Leave blank for local / no-key providers (e.g. Ollama)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCloseConfigModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { AISettingsPage };